import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, MessageSquare, Film, Clock, Lock, Send, Loader2, BarChart3, Sparkles, SlidersHorizontal } from "lucide-react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { PollComposer, pollPayload } from "./PollComposer";
import { stashTransfer } from "./MoveToVault";

const DESTS = [
  { key: "hangout", label: "Hangout", desc: "Post to the community feed", icon: MessageSquare },
  { key: "memory", label: "Memory", desc: "Save as a story", icon: Film },
  { key: "timecapsule", label: "Time Capsule", desc: "Preserve for the future", icon: Clock },
  { key: "secret_vault", label: "Secret Vault", desc: "Schedule a private release", icon: Lock },
];

const TC_MODES = [
  { key: "interview", label: "Interview" },
  { key: "wisdom", label: "Words of Wisdom" },
  { key: "challenges", label: "Life Challenge" },
  { key: "favorites", label: "Favorite Things" },
  { key: "greeting", label: "Greeting" },
];

export function ShareVideoSheet({ videoPath, description, transcript, onClose }) {
  const [dest, setDest] = useState(null);
  const [caption, setCaption] = useState("");
  const [tcMode, setTcMode] = useState(null);
  const [addPoll, setAddPoll] = useState(false);
  const [pollQuestions, setPollQuestions] = useState([{ type: "mc", text: "", options: ["", ""] }]);
  const [busy, setBusy] = useState(false);

  const clip = { type: "video", media: videoPath };

  const finish = (msg) => { toast.success(msg); onClose(); };

  const postMemory = async () => {
    setBusy(true);
    try {
      await api.post("/memories", { type: "story", caption: caption.trim() || description || "A Respawner video", media: videoPath, media_kind: "video" });
      finish("Posted to Memories");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post"); }
    finally { setBusy(false); }
  };

  const postHangout = async () => {
    setBusy(true);
    try {
      const payload = { content: caption.trim() || description || "Shared a video", video: videoPath };
      if (addPoll) { const p = pollPayload(pollQuestions); if (p) payload.poll = p; }
      await api.post("/hangouts", payload);
      finish("Posted to Hangouts");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post"); }
    finally { setBusy(false); }
  };

  const postTimeCapsule = async () => {
    if (!tcMode) { toast.error("Pick a type first"); return; }
    setBusy(true);
    try {
      const label = TC_MODES.find((m) => m.key === tcMode)?.label || "Entry";
      await api.post("/timecapsule/entry", {
        mode: tcMode,
        title: caption.trim() || label,
        data: { caption: caption.trim() || null, clip },
      });
      finish(`Saved to your Time Capsule (${label})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const moveToVault = (mode) => {
    stashTransfer({ mode, media: { path: videoPath, kind: "video", title: caption.trim() || description || "Respawner video", source: "respawner", transcript: transcript || null } });
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }}
        onClick={onClose} data-testid="share-video-sheet">
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[88vh] overflow-y-auto no-scrollbar"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {dest && (
                <button onClick={() => { setDest(null); setTcMode(null); }} data-testid="share-back"
                  className="p-1 -ml-1" style={{ color: "var(--text-secondary)" }}><ArrowLeft size={18} /></button>
              )}
              <h3 className="font-serif-display text-xl">{dest ? DESTS.find((d) => d.key === dest)?.label : "Share video"}</h3>
            </div>
            <button onClick={onClose} data-testid="share-close" className="p-1" style={{ color: "var(--text-secondary)" }}><X size={20} /></button>
          </div>

          <div className="mb-4 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <video src={fileUrl(videoPath)} controls className="w-full" style={{ background: "#000", maxHeight: 200 }} />
          </div>

          {/* Destination chooser */}
          {!dest && (
            <div className="grid grid-cols-2 gap-3" data-testid="share-dest-list">
              {DESTS.map((d) => (
                <button key={d.key} data-testid={`share-dest-${d.key}`}
                  onClick={() => setDest(d.key)}
                  className="flex flex-col items-start gap-1.5 rounded-2xl p-4 text-left transition-colors"
                  style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
                  <d.icon size={20} className="gold-text" />
                  <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{d.label}</span>
                  <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{d.desc}</span>
                </button>
              ))}
            </div>
          )}

          {/* Memory */}
          {dest === "memory" && (
            <div className="space-y-3" data-testid="share-memory">
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>This will post as a <span className="gold-text font-semibold">Story</span> on the Memories page.</p>
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} data-testid="share-memory-caption"
                placeholder="Add a comment to your story…"
                className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
                style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
              <button onClick={postMemory} disabled={busy} data-testid="share-memory-post"
                className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "#fff" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Post story
              </button>
            </div>
          )}

          {/* Hangout */}
          {dest === "hangout" && (
            <div className="space-y-3" data-testid="share-hangout">
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} data-testid="share-hangout-caption"
                placeholder="Say something about this…"
                className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
                style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
              <label className="flex items-center justify-between rounded-xl px-4 py-2.5" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
                <span className="text-sm flex items-center gap-1.5"><BarChart3 size={15} className="gold-text" /> Add a poll</span>
                <input type="checkbox" checked={addPoll} onChange={(e) => setAddPoll(e.target.checked)} data-testid="share-hangout-poll-toggle" className="h-4 w-4 accent-[var(--accent)]" />
              </label>
              {addPoll && <div className="rounded-xl p-3" style={{ background: "var(--overlay)" }}><PollComposer questions={pollQuestions} onChange={setPollQuestions} /></div>}
              <button onClick={postHangout} disabled={busy} data-testid="share-hangout-post"
                className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "#fff" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Post to Hangouts
              </button>
            </div>
          )}

          {/* Time Capsule */}
          {dest === "timecapsule" && (
            <div className="space-y-3" data-testid="share-timecapsule">
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>What kind of entry is this?</p>
              <div className="grid grid-cols-2 gap-2" data-testid="share-tc-modes">
                {TC_MODES.map((m) => (
                  <button key={m.key} data-testid={`share-tc-mode-${m.key}`} onClick={() => setTcMode(m.key)}
                    className="rounded-xl py-2.5 text-xs font-semibold transition-colors"
                    style={tcMode === m.key ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                    {m.label}
                  </button>
                ))}
              </div>
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2} data-testid="share-tc-caption"
                placeholder="Add a caption (optional)…"
                className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
                style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
              <button onClick={postTimeCapsule} disabled={busy || !tcMode} data-testid="share-tc-post"
                className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold"
                style={{ background: tcMode ? "var(--accent)" : "var(--overlay)", color: tcMode ? "#fff" : "var(--text-muted)" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Save to Time Capsule
              </button>
            </div>
          )}

          {/* Secret Vault */}
          {dest === "secret_vault" && (
            <div className="space-y-3" data-testid="share-secret-vault">
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2} data-testid="share-sv-caption"
                placeholder="Give it a title (optional)…"
                className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
                style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>How would you like to set up its release?</p>
              <button onClick={() => moveToVault("guide")} data-testid="share-sv-guide"
                className="w-full flex items-center gap-3 rounded-xl p-3 text-left" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
                <Sparkles size={18} className="gold-text" /><div><div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Guide me</div><div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Let the AI concierge recommend the best plan</div></div>
              </button>
              <button onClick={() => moveToVault("manual")} data-testid="share-sv-manual"
                className="w-full flex items-center gap-3 rounded-xl p-3 text-left" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
                <SlidersHorizontal size={18} className="gold-text" /><div><div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Manual</div><div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Pick recipients and conditions yourself</div></div>
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
